import { useCallback, useRef, useState } from 'react'

// One-at-a-time guard for a checkout launch that ends in a full-page redirect.
//
// The policy is deliberately asymmetric, and both halves matter:
//
//   * SUCCESS LATCHES. A successful handoff means the browser is on its way to the
//     gateway, so there is nothing left to re-enable — releasing here would re-arm
//     the CTA for the whole duration of the navigation.
//   * FAILURE RELEASES, so the customer can retry rather than being left with a
//     dead button.
//
// The ref is what makes it airtight. Two clicks inside one tick both read the same
// pre-render `isLaunching`, so state alone cannot reject the second; the ref is
// written before the first await. The state exists only so consumers re-render.
//
// Kept in its own module, importing nothing but React, so it can be unit-tested at
// all: its consumer reaches billing-service, the toast module and pages/billing/queries,
// each of which a sibling test file replaces process-globally with a partial
// mock.module (AGENTS.md failure mode 1). See use-launch-guard.test.tsx.
//
// Note recharge-modal.tsx (the Cashfree modal) hand-rolls this same shape inline and
// predates this hook; adopting it there is a fair follow-up, not part of this change.

export interface LaunchOutcome {
  ok: boolean
}

export const useLaunchGuard = () => {
  const [isLaunching, setIsLaunching] = useState(false)
  const inFlightRef = useRef(false)

  const release = useCallback(() => {
    inFlightRef.current = false
    setIsLaunching(false)
  }, [])

  // Resolves to the task's own outcome, or `undefined` when the call was suppressed.
  // Callers must tell those apart: a suppressed duplicate is not a failure, and
  // reporting one would show "Payment Failed" to someone whose first click is still
  // working.
  const launch = useCallback(
    async <T extends LaunchOutcome>(task: () => Promise<T>): Promise<T | undefined> => {
      if (inFlightRef.current) return undefined
      inFlightRef.current = true
      setIsLaunching(true)

      let result: T
      try {
        result = await task()
      } catch (error) {
        // Nothing was handed off, so the guard must not stay engaged. Rethrow —
        // swallowing here would turn a crash into a silent no-op.
        release()
        throw error
      }

      if (!result.ok) release()
      return result
    },
    [release],
  )

  return { launch, isLaunching }
}
